//src/components/CampaignExport.tsx
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { FileSpreadsheet, FileText, File } from 'lucide-react';
import { format } from 'date-fns';

interface Campaign {
  id: string;
  name: string;
  metrics: {
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: number;
    cpc: number;
    cpa: number;
    budget?: number;
    roi?: number;
    frequency?: number;
  };
  dateRange: {
    start: string;
    end: string;
  };
  platform: 'facebook' | 'google' | 'tiktok';
}

interface CampaignExportProps {
  campaigns: Campaign[];
  dateRange: { from: Date; to: Date };
}

export const CampaignExport: React.FC<CampaignExportProps> = ({ campaigns, dateRange }) => {
  const generateCSV = () => {
    const headers = [
      'Campaign Name',
      'Platform',
      'Date Range',
      'Spend',
      'Budget',
      'Impressions',
      'Clicks',
      'Conversions',
      'CTR',
      'CPC',
      'CPA',
      'ROI',
      'Frequency'
    ];

    const rows = campaigns.map(campaign => [
      campaign.name,
      campaign.platform,
      `${campaign.dateRange.start} to ${campaign.dateRange.end}`,
      campaign.metrics.spend.toFixed(2),
      campaign.metrics.budget?.toFixed(2) || 'N/A',
      campaign.metrics.impressions,
      campaign.metrics.clicks,
      campaign.metrics.conversions,
      `${campaign.metrics.ctr.toFixed(2)}%`,
      campaign.metrics.cpc.toFixed(2),
      campaign.metrics.cpa.toFixed(2),
      campaign.metrics.roi?.toFixed(2) || 'N/A',
      campaign.metrics.frequency?.toFixed(2) || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `campaign_report_${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const generatePerformanceReport = () => {
    // Calculate overall metrics
    const totalMetrics = campaigns.reduce((acc, campaign) => ({
      spend: acc.spend + campaign.metrics.spend,
      impressions: acc.impressions + campaign.metrics.impressions,
      clicks: acc.clicks + campaign.metrics.clicks,
      conversions: acc.conversions + campaign.metrics.conversions
    }), { spend: 0, impressions: 0, clicks: 0, conversions: 0 });

    const reportContent = `
Campaign Performance Report
${format(dateRange.from, 'dd/MM/yyyy')} to ${format(dateRange.to, 'dd/MM/yyyy')}

Overall Performance
------------------
Total Spend: R$ ${totalMetrics.spend.toFixed(2)}
Total Impressions: ${totalMetrics.impressions}
Total Clicks: ${totalMetrics.clicks}
Total Conversions: ${totalMetrics.conversions}
Overall CTR: ${((totalMetrics.clicks / totalMetrics.impressions) * 100).toFixed(2)}%
Overall CPA: R$ ${(totalMetrics.spend / totalMetrics.conversions).toFixed(2)}

Campaign Breakdown
----------------
${campaigns.map(campaign => `
${campaign.name} (${campaign.platform})
- Spend: R$ ${campaign.metrics.spend.toFixed(2)}
- Conversions: ${campaign.metrics.conversions}
- CPA: R$ ${campaign.metrics.cpa.toFixed(2)}
- CTR: ${campaign.metrics.ctr.toFixed(2)}%
`).join('\n')}
`;

    const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `performance_report_${format(dateRange.from, 'yyyy-MM-dd')}.txt`;
    link.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Exportar Relatórios</CardTitle>
        <CardDescription>
          Escolha o formato desejado para exportar os dados das campanhas
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          <Button
            onClick={generateCSV}
            className="flex items-center justify-center gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Exportar CSV
          </Button>

          <Button
            onClick={generatePerformanceReport}
            variant="outline"
            className="flex items-center justify-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Relatório de Performance
          </Button>

          <Button
            onClick={() => window.print()}
            variant="outline"
            className="flex items-center justify-center gap-2"
          >
            <File className="w-4 h-4" />
            Imprimir Dashboard
          </Button>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium mb-2">O que está incluído nos relatórios?</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Métricas detalhadas de todas as campanhas</li>
            <li>• Análise de performance por plataforma</li>
            <li>• Dados de conversão e ROI</li>
            <li>• Insights de otimização</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};